import React, { useMemo, useState } from "react";
import Snackbar from "../../../../common/Snackbar";
import {
  Button,
  Col,
  Modal,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";
import MultilineTextBox from "../../../../common/MultilineTextBox";
import AccordionComponent from "../../../../common/Accordion";
import DataTable from "../../../../common/DataTable";
import {
  downloadClientInvoice,
  downloadRoleInvoice,
  downloadUserInvoice,
  generateInvoiceByUniqueKey,
} from "../../../../services/InvoiceAPI";
import { toYearMonth } from "../../../../utils/dateUtils";
import UploadStatusFloatingIcon from "../../../../common/UploadStatusFloatingIcon";
import TruncateWithTooltip from "../../../../common/TruncateWithTooltip";

type Header = {
  key: string;
  label: string;
  sortable?: boolean;
  style?: React.CSSProperties;
  isNumeric?: boolean;
};

export const HEADERS: Header[] = [
  {
    key: "invoiceNo",
    label: "Client",
  },
  {
    key: "clinetName",
    label: "Generated On | By",
  },
  {
    key: "billingPeriod",
    label: "Billing Period",
  },
];

const GenerateInvoice = ({
  show,
  onClose,
  selectedRow,
  month,
  activeTab,
  clientInvoiceSummaries,
  userInfo,
}: any) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const [downloadStatuses, setDownloadStatuses] = useState<any>({});

  const [invoiceReportsKey, setInvoiceReportsKey] = useState<string | null>(
    "0",
  );
  const [isSaving, setIsSaving] = useState(false);

  const selectedClientId = selectedRow?.[0]?.clientId;

  const matchedClient = clientInvoiceSummaries.find(
    (client: any) => client.clientId === selectedClientId,
  );

  const clientName = matchedClient?.clientName;

  const monthDate = new Date(month);

  const billingPeriod = monthDate.toLocaleString("default", {
    month: "short",
    year: "numeric",
  });

  const headerInfo = useMemo(() => {
    const now = new Date();

    const formattedDateTime = now
      ?.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");

    const fullName =
      `${userInfo?.salutationTypeDesc || ""} ${userInfo?.firstName || ""} ${userInfo?.lastName || ""}`?.trim();
    const role = userInfo?.userRoleDescription || "";

    return `${formattedDateTime} | ${fullName}, (${role})`;
  }, [userInfo]);

  const handleDownloadProgress = async (
    id: string,
    downloadFn: () => Promise<any>,
    fileName: string = "File.zip",
  ) => {
    setDownloadStatuses((prev: any) => ({
      ...prev,
      [id]: { fileName, progress: 0, isUploading: true },
    }));

    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      if (progress >= 90) return;
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], progress },
      }));
    }, 300);

    try {
      await downloadFn();
      clearInterval(interval);
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], progress: 100, isUploading: false },
      }));
      setTimeout(() => {
        setDownloadStatuses((prev: any) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
      }, 2000);
    } catch (error) {
      console.error("Download error:", error);
      clearInterval(interval);
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], isUploading: false },
      }));
    }
  };

  const extractInvoiceIds = (message: string): number[] => {
    const match = message.match(/Invoice IDs\s*=\s*([0-9,]+)/);
    if (!match) return [];

    return match[1]
      .split(",")
      .map((id) => Number(id.trim()))
      .filter(Boolean);
  };

  const onSave = async () => {
    if (isSaving) return;
    try {
      setIsSaving(true);
      const uniqueKeyLookupId = selectedRow
        .map((item: any) => item.uniqueKey_LookupId)
        .filter(Boolean)
        .join(",");

      const generateInvoicePayload = {
        invoicePeriod: toYearMonth(month),
        uniqueKeyLookupId,
      };

      const response = await generateInvoiceByUniqueKey(generateInvoicePayload);

      if (!response?.isSuccess) return;

      setError("success");
      setSnackbarMessage("Invoice has been generated.");
      setSnackbarOpen(true);

      const invoiceIds = extractInvoiceIds(
        response?.result?.responseMessage || "",
      );

      if (activeTab == "Client") {
        const downloadPromises = invoiceIds.map((invoiceId) =>
          handleDownloadProgress(
            `invoice-${invoiceId}`,
            () => downloadClientInvoice(invoiceId, true),
            `Client-Invoice-${invoiceId}.pdf`,
          ),
        );

        await Promise.all(downloadPromises);

        onClose?.("Save");
      } else if (activeTab == "Role") {
        const downloadPromises = invoiceIds.map((invoiceId) =>
          handleDownloadProgress(
            `invoice-${invoiceId}`,
            () => downloadRoleInvoice(invoiceId, true),
            `Client-Invoice-${invoiceId}.pdf`,
          ),
        );

        await Promise.all(downloadPromises);

        onClose?.("Save");
      } else if (activeTab == "User") {
        const downloadPromises = invoiceIds.map((invoiceId) =>
          handleDownloadProgress(
            `invoice-${invoiceId}`,
            () => downloadUserInvoice(invoiceId, true),
            `Client-Invoice-${invoiceId}.pdf`,
          ),
        );

        await Promise.all(downloadPromises);

        onClose?.("Save");
      }
    } catch (error) {
      console.error("Error generating invoices:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const isDownloading = Object.values(downloadStatuses || {}).some(
    (item: any) => item.isUploading,
  );

  const handleAccordionSelect = (key: string | null) => {
    if (invoiceReportsKey === key) {
      return;
    }
    setInvoiceReportsKey(key || "0");
  };

  const configurationAccordion = (
    items: { title: string; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item) => ({ ...item }))}
      activeKey={invoiceReportsKey}
      onSelect={handleAccordionSelect}
    />
  );

  return (
    <>
      <Snackbar
        message={snackbarMessage}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={error}
      />
      <UploadStatusFloatingIcon
        uploadStatuses={downloadStatuses}
        isUpload={false}
        onCancelUpload={(id) => {
          setDownloadStatuses((prev: any) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        }}
      />
      <Modal
        show={show}
        onClose={onClose}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="recordList"
      >
        <Modal.Body className="fw-bold text-center modelheading ">
          <p
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                border: "1px solid #f44336",
                backgroundColor: "#fdecea",
                fontSize: "15px",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              {selectedRow.length}
            </span>
            Line items will be invoiced. Do you want to continue?
          </p>

          {selectedRow.length == 1 && (
            <>
              {configurationAccordion([
                {
                  title: "Details",
                  content: (
                    <>
                      <div className="table-responsive border mb-2">
                        <Table>
                          <thead>
                            <tr>
                              <th
                                style={{
                                  width: "40%",
                                  textAlign: "left",
                                  fontWeight: "500 !important",
                                }}
                              >
                                Client
                              </th>
                              <th
                                style={{
                                  width: "45%",
                                  textAlign: "left",
                                  fontWeight: "500 !important",
                                }}
                              >
                                Generate On | By
                              </th>
                              <th
                                style={{
                                  textAlign: "left",
                                  fontWeight: "500 !important",
                                }}
                              >
                                Billing Period
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            <tr>
                              <td className="text-start">
                                <TruncateWithTooltip className="text-truncate truncatetwoline generateGridd">
                                  <span>{clientName}</span>
                                </TruncateWithTooltip>
                              </td>

                              <td className="text-start">
                                <TruncateWithTooltip className="text-truncate truncatetwoline generateGridd">
                                  <span>{headerInfo}</span>
                                </TruncateWithTooltip>
                              </td>

                              <td className="text-start">
                                <TruncateWithTooltip className="truncatetwoline generateGridd">
                                  <span>{billingPeriod}</span>
                                </TruncateWithTooltip>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </>
                  ),
                },
              ])}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top d-flex justify-content-end">
          <Button
            variant="outline-info"
            className="d-flex align-items-end"
            onClick={onClose}
            disabled={isDownloading}
          >
            <span className="material-icons mr-2">close</span> Cancel
          </Button>
          <Button
            variant="primary"
            className="ms-3 d-flex align-items-center"
            onClick={onSave}
            disabled={isDownloading || isSaving}
          >
            <span className="material-icons mx-1">check</span> Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GenerateInvoice;
